import { useRouter } from 'next/router';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import {
  Alert,
  Badge,
  ConfirmButton,
  EmptyIndicator,
  FlagIcon,
  FlexContainer,
  MoreOptions,
  Pagination,
  ProgressBar
} from 'components/common';
import { Dropdown } from 'components/common/dropdown';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableRow,
  BoxTableHeader,
  BoxOverflowWrapper
} from 'components/common/box';
import { AIGenerateTranslations } from 'components/aiTools';
import {
  SettingsAddOrEditLanguageVersion,
  SettingsExportLanguageVersions,
  SettingsImportLanguageVersions,
  SettingsMergeLanguageVersion
} from '.';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';

const PAGE_SIZE = 10;

interface Props {
  loading: boolean;
  translationProgressData: GetCourseTranslationProgressRes['progressByLanguage'];
  updateTranslationProgress: () => Promise<void>;
}

const SettingsLanguageVersions = ({
  loading,
  translationProgressData,
  updateTranslationProgress
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    theme,
    user
  } = useReduxState();

  const router = useRouter();
  const pageFromQuery = parseInt(router.query.page as string) || 1;

  const languageOptions = getCourseLanguageVersionOptions({ course });

  const languageVersionKeys = course.languageVersions
    ? Object.entries(course.languageVersions)
    : [];

  // sort alphabetically
  const sortedLanguageVersionKeys = languageVersionKeys
    .slice()
    .sort(([langA], [langB]) => {
      const labelA = languageOptions[langA]?.capitalizedName || langA;
      const labelB = languageOptions[langB]?.capitalizedName || langB;
      return labelA.localeCompare(labelB);
    });

  const numberOfPages = Math.ceil(sortedLanguageVersionKeys.length / PAGE_SIZE);

  const paginatedEntries = sortedLanguageVersionKeys.slice(
    (pageFromQuery - 1) * PAGE_SIZE,
    pageFromQuery * PAGE_SIZE
  );

  const handlePublishLanguageVersion = async (
    language: string,
    publish: boolean
  ) => {
    try {
      await api.put(`/admin/courses/${course.id}/edit-language-version`, {
        language,
        isPublished: publish
      });
      const courses = await api.get(`/admin/courses?id=${course.id}`);

      dispatch.updatePageState({
        course: courses.data.courses[0],
        hasUnsavedChanges: false
      });

      dispatch.addNotice({
        text: publish
          ? messages.courseLanguageVersionsPublishSuccess
          : messages.courseLanguageVersionsUnpublishSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const handleDeleteLanguageVersion = async (language: string) => {
    try {
      await api.delete(`/admin/courses/${course.id}/delete-language-version`, {
        data: { language }
      });
      const courses = await api.get(`/admin/courses?id=${course.id}`);

      dispatch.updatePageState({
        course: courses.data.courses[0],
        hasUnsavedChanges: false
      });
      dispatch.addNotice({
        text: messages.courseLanguageVersionsDeleteSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const handleSetAsDefaultLanguage = async (lang: string) => {
    try {
      await api.put(`/admin/courses/${course.id}`, { language: lang });
      const courses = await api.get(`/admin/courses?id=${course.id}`);

      dispatch.updatePageState({
        course: courses.data.courses[0],
        hasUnsavedChanges: false
      });

      await updateTranslationProgress();

      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <div style={{ marginBottom: numberOfPages > 1 ? 120 : 160 }}>
      <Box
        headerContent={
          <FlexContainer>
            <SettingsAddOrEditLanguageVersion
              key="addLanguageVersion"
              action="add"
              buttonText={messages.courseLanguageVersionsAdd}
              buttonStyle={{
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                marginRight: 1
              }}
              icon="plus"
              successMessage={messages.courseLanguageVersionsAddSuccess}
            />

            <Dropdown
              buttonStyle={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0
              }}
              category="success"
              icon="chevron-down"
              size="header"
              styleContentButtons={false}
            >
              <SettingsMergeLanguageVersion
                onMergeSuccess={updateTranslationProgress}
              />
            </Dropdown>
          </FlexContainer>
        }
        loading={loading}
        style={{ marginTop: 25 }}
        title={messages.courseLanguageVersions}
        padding={0}
      >
        {languageVersionKeys.length < 1 ? (
          <EmptyIndicator
            icon="language"
            title={messages.courseLanguageVersionsNotFound}
          />
        ) : (
          <>
            <BoxOverflowWrapper>
              <BoxTable>
                <BoxTableHeader>
                  <BoxTableCell>{messages.commonLanguage}</BoxTableCell>

                  <BoxTableCell>{messages.commonTitle}</BoxTableCell>

                  <BoxTableCell>
                    {messages.courseLanguageVersionsTranslationProgress}
                  </BoxTableCell>

                  <BoxTableCell>{messages.commonPublished}</BoxTableCell>

                  <BoxTableCell shrink={true} />
                </BoxTableHeader>

                {paginatedEntries.map(([lang, data]) => {
                  const languageOption = languageOptions[lang];
                  const languageLabel = languageOption?.capitalizedName || lang;

                  return (
                    <BoxTableRow key={lang}>
                      <BoxTableCell>
                        <span className="lang-flag-label">
                          <FlagIcon
                            lang={lang}
                            style={{ marginRight: 6, marginTop: 1 }}
                          />

                          <span className="lang-label">{languageLabel}</span>
                        </span>
                      </BoxTableCell>

                      <BoxTableCell>{data.courseTitle || '-'}</BoxTableCell>

                      <BoxTableCell>
                        <ProgressBar
                          color={
                            translationProgressData?.[lang]?.totalProgress < 100
                              ? theme.warningStandaloneColor
                              : theme.successStandaloneColor
                          }
                          horizontal={true}
                          label={
                            translationProgressData?.[lang]?.totalProgress !==
                            undefined
                              ? `${translationProgressData[lang]?.totalProgress}%`
                              : '0%'
                          }
                          labelStyle={{
                            color: theme.textColorLight,
                            fontSize: '1.5rem',
                            textAlign: 'right',
                            width: 40
                          }}
                          progress={
                            translationProgressData?.[lang]?.totalProgress !==
                            undefined
                              ? translationProgressData[lang]?.totalProgress /
                                100
                              : 0
                          }
                          progressStyle={{ marginTop: 5, width: 60 }}
                        />
                      </BoxTableCell>

                      <BoxTableCell>
                        {data.isPublished ? (
                          <Badge
                            category="success-light"
                            text={messages.commonPublished}
                          />
                        ) : (
                          <Badge
                            category="warning-light"
                            text={messages.commonNotPublished}
                          />
                        )}
                      </BoxTableCell>

                      <BoxTableCell>
                        <MoreOptions
                          renderAsFixed={true}
                          styleContentButtons={false}
                        >
                          <SettingsAddOrEditLanguageVersion
                            action="edit"
                            buttonIsDropdown={true}
                            buttonText={messages.commonEdit}
                            icon="pen"
                            languageVersion={lang}
                            languageVersionData={data}
                            languageVersionLabel={languageLabel}
                            successMessage={
                              messages.courseLanguageVersionsEditSuccess
                            }
                          />

                          {data.isPublished ? (
                            <ConfirmButton
                              actionCategory="danger"
                              actionText={messages.commonUnpublish}
                              buttonIsDropdown={true}
                              buttonIcon="times-circle"
                              buttonText={messages.commonUnpublish}
                              category="light"
                              confirmText={
                                <div>
                                  <Alert
                                    style={{ marginBottom: 15 }}
                                    text={
                                      messages.courseLanguageVersionsUnpublishWarning
                                    }
                                    type="warning-light"
                                  />
                                  {
                                    messages.courseLanguageVersionsUnpublishConfirm
                                  }
                                </div>
                              }
                              confirmTitle={
                                messages.courseLanguageVersionsUnpublish
                              }
                              onConfirm={async () => {
                                await handlePublishLanguageVersion(lang, false);
                                return true;
                              }}
                            />
                          ) : (
                            <ConfirmButton
                              actionCategory="success"
                              actionText={messages.commonPublish}
                              buttonIsDropdown={true}
                              buttonIcon="check-circle"
                              buttonText={messages.commonPublish}
                              category="light"
                              confirmText={
                                <div>
                                  {translationProgressData?.[lang]
                                    ?.totalProgress !== 100 ? (
                                    <Alert
                                      style={{ marginBottom: 15 }}
                                      text={
                                        messages.courseLanguageVersionsPublishWarning
                                      }
                                      type="warning-light"
                                    />
                                  ) : null}

                                  {
                                    messages.courseLanguageVersionsPublishConfirm
                                  }
                                </div>
                              }
                              confirmTitle={
                                messages.courseLanguageVersionsPublish
                              }
                              onConfirm={async () => {
                                await handlePublishLanguageVersion(lang, true);
                                return true;
                              }}
                            />
                          )}

                          {lang !== course.language && (
                            <ConfirmButton
                              actionCategory="success"
                              actionText={
                                messages.courseLanguageVersionsDefaultSetAsDefault
                              }
                              buttonIsDropdown={true}
                              buttonIcon="flag"
                              buttonText={
                                messages.courseLanguageVersionsDefaultSetAsDefault
                              }
                              buttonTooltip={
                                translationProgressData?.[lang]
                                  ?.totalProgress !== 100
                                  ? messages.courseLanguageVersionsDefaultDisabledTooltip
                                  : undefined
                              }
                              confirmText={
                                <div>
                                  <Alert
                                    style={{ marginBottom: 15 }}
                                    text={
                                      messages.courseLanguageVersionsDefaultSetAsDefaultInfo
                                    }
                                    type="info"
                                  />

                                  {
                                    messages.courseLanguageVersionsDefaultSetAsDefaultConfirm
                                  }
                                </div>
                              }
                              confirmTitle={
                                messages.courseLanguageVersionsDefaultSetAsDefault
                              }
                              disabled={
                                translationProgressData?.[lang]
                                  ?.totalProgress !== 100
                              }
                              onConfirm={async () => {
                                await handleSetAsDefaultLanguage(lang);
                                return true;
                              }}
                            />
                          )}

                          <AIGenerateTranslations
                            onTranslationSuccess={updateTranslationProgress}
                            targetLanguage={lang}
                          />

                          {course.isOwnerOrSuperadmin ? (
                            <>
                              <SettingsExportLanguageVersions
                                buttonIsDropdown={true}
                                targetLanguage={lang}
                              />

                              <SettingsImportLanguageVersions
                                onImportSuccess={updateTranslationProgress}
                                targetLanguage={lang}
                              />
                            </>
                          ) : null}

                          <ConfirmButton
                            actionCategory="danger"
                            actionText={messages.commonDelete}
                            buttonIsDropdown={true}
                            buttonIcon="trash-alt"
                            buttonText={messages.commonDelete}
                            category="light"
                            confirmText={
                              messages.courseLanguageVersionsDeleteVersionsWarning
                            }
                            confirmTitle={messages.courseLanguageVersionsDelete}
                            onConfirm={async () => {
                              await handleDeleteLanguageVersion(lang);
                              return true;
                            }}
                          />
                        </MoreOptions>
                      </BoxTableCell>
                    </BoxTableRow>
                  );
                })}
              </BoxTable>
            </BoxOverflowWrapper>
          </>
        )}
      </Box>

      {numberOfPages > 1 && (
        <Pagination
          currentPage={pageFromQuery}
          numberOfPages={numberOfPages}
          onPageChange={({ page }) => {
            router.replace({ query: { ...router.query, page } }, undefined, {
              shallow: true
            });
          }}
        />
      )}

      <style jsx>{`
        .lang-flag-label {
          display: flex;
          align-items: center;
        }
        .lang-label {
          display: inline;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .lang-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default SettingsLanguageVersions;
